import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  SelectAll as SelectAllIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Add as AddIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { format, addDays } from 'date-fns';
import whatsappService from '../services/whatsapp.service';
import { fetchWithCacheFallback } from '../services/api-client.service';
import { offlineCacheService } from '../services/offline-cache.service';
import { CACHE_KEYS } from '../config/cache-config';
import { useMobileShell } from '../hooks/useMobileShell';

interface PaymentRow {
  id: string;
  residentName: string;
  bedNumber: string;
  phoneNumber: string;
  dueDate: Date;
  amount: number;
  paidAmount: number;
  paymentStatus: string;
  gracePeriodEnd: Date;
}

interface ResidentOption {
  id: string;
  name: string;
  bedNumber: string;
  monthlyRent: number;
}

type PaymentTabFilter = 'all' | 'pending' | 'overdue' | 'paid';

const Payments: React.FC = () => {
  const isMobile = useMobileShell();
  const [paymentData, setPaymentData] = useState<PaymentRow[]>([]);
  const [residents, setResidents] = useState<ResidentOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
  const [whatsAppApiConfigured, setWhatsAppApiConfigured] = useState(false);
  const [bulkReminderDialog, setBulkReminderDialog] = useState(false);
  const [recordPaymentDialog, setRecordPaymentDialog] = useState(false);
  const [paymentTab, setPaymentTab] = useState<PaymentTabFilter>('all');
  const [selectedMonthYear, setSelectedMonthYear] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [recordPaymentForm, setRecordPaymentForm] = useState({
    residentId: '',
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Cash' as 'Online' | 'Cash',
  });

  const openRecordPaymentDialog = (prefillResident?: PaymentRow) => {
    if (prefillResident) {
      const resident = residents.find(r => String(r.id) === String(prefillResident.id));
      setRecordPaymentForm({
        residentId: prefillResident.id,
        amount: resident?.monthlyRent ?? prefillResident.amount ?? 0,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'Cash',
      });
    } else {
      setRecordPaymentForm({
        residentId: '',
        amount: 0,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'Cash',
      });
    }
    setRecordPaymentDialog(true);
  };
  const [recordPaymentSubmitting, setRecordPaymentSubmitting] = useState(false);

  const loadPaymentData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [year, month] = selectedMonthYear.split('-').map(Number);
      const monthYear = selectedMonthYear;
      const dueDate = new Date(year, month - 1, 1);
      const gracePeriodEnd = addDays(dueDate, 5);
      const now = new Date();

      const [residentsRes, paymentsRes] = await Promise.all([
        fetchWithCacheFallback<{ success: boolean; data: { id: string; name: string; bedNumber: string; contactNumber: string; monthlyRent: number }[]; pagination?: { total: number } }>(
          '/api/residents?status=active&limit=100'
        ),
        fetchWithCacheFallback<{ success: boolean; data: { residentId: string; amount: number; status: string }[] }>(
          `/api/payments?monthYear=${monthYear}`
        ),
      ]);

      const residentsList = Array.isArray(residentsRes.data?.data) ? residentsRes.data.data : (Array.isArray(residentsRes.data) ? residentsRes.data : []);
      const paymentsList = Array.isArray(paymentsRes.data?.data) ? paymentsRes.data.data : (Array.isArray(paymentsRes.data) ? paymentsRes.data : []);

      interface ResidentData {
        id?: string | number;
        name?: string;
        bedNumber?: string | number;
        monthlyRent?: number;
        contactNumber?: string;
      }

      setResidents(residentsList.map((r: ResidentData) => ({
        id: String(r.id ?? ''),
        name: r.name ?? '',
        bedNumber: String(r.bedNumber ?? ''),
        monthlyRent: r.monthlyRent ?? 0,
      })));

      const paidByResident = new Map<string, number>();
      for (const p of paymentsList) {
        if (p.status === 'COMPLETED' || p.status === 'completed' || p.status === 'paid') {
          paidByResident.set(p.residentId, (paidByResident.get(p.residentId) ?? 0) + p.amount);
        }
      }

      const rows: PaymentRow[] = residentsList.map((r: ResidentData, idx: number) => {
        const amount = r.monthlyRent ?? 0;
        const residentId = String(r.id ?? `r-${idx}`);
        const paidAmount = r.id ? (paidByResident.get(String(r.id)) ?? 0) : 0;
        let paymentStatus = 'due_soon';
        if (paidAmount >= amount) paymentStatus = 'paid';
        else if (paidAmount > 0) paymentStatus = 'partial';
        else if (now > gracePeriodEnd) paymentStatus = 'overdue';

        return {
          id: residentId,
          residentName: r.name ?? '',
          bedNumber: String(r.bedNumber ?? ''),
          phoneNumber: r.contactNumber ?? '',
          dueDate,
          amount,
          paidAmount,
          paymentStatus,
          gracePeriodEnd,
        };
      });

      setPaymentData(rows);
    } catch (err) {
      setLoadError('Failed to load payment data');
      setPaymentData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMonthYear]);

  useEffect(() => {
    loadPaymentData();
    checkWhatsAppConfiguration();
  }, [loadPaymentData]);

  const checkWhatsAppConfiguration = async () => {
    try {
      const config = await whatsappService.checkConfiguration();
      setWhatsAppApiConfigured(config.apiConfigured);
    } catch (error) {
      console.error('Failed to check WhatsApp configuration:', error);
      setWhatsAppApiConfigured(false);
    }
  };

  const sendManualReminder = async (resident: PaymentRow) => {
    try {
      const template = resident.paymentStatus === 'overdue' ? 'urgent' : 'standard';
      await whatsappService.sendManualReminder({ ...resident, id: parseInt(resident.id, 10) || 0 }, template);
    } catch (error) {
      console.error('Failed to send manual reminder:', error);
      alert('Failed to send reminder. Please check if WhatsApp is available on your device.');
    }
  };

  const handleBulkReminder = () => {
    if (selectedPayments.length === 0) {
      alert('Please select residents to send reminders to.');
      return;
    }
    
    setBulkReminderDialog(true);
  };

  const processBulkReminders = async () => {
    const selectedResidents = selectedPayments
      .map(id => paymentData.find(p => p.id === id))
      .filter((resident): resident is PaymentRow => resident !== undefined);

    if (selectedResidents.length === 0) {
      alert('No residents selected for reminders.');
      return;
    }

    try {
      const residentsForReminder = selectedResidents.map((r) => ({
        ...r,
        id: parseInt(r.id, 10) || 0,
      }));
      await whatsappService.sendBulkManualReminders(
        residentsForReminder,
        'standard',
        1000
      );
      
      setBulkReminderDialog(false);
      setSelectedPayments([]);
      
      // Show success message
      alert(`Bulk reminders initiated for ${selectedResidents.length} residents. WhatsApp will open for each resident.`);
    } catch (error) {
      console.error('Failed to process bulk reminders:', error);
      alert('Failed to process bulk reminders. Please try again.');
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'paid': return 'success';
      case 'overdue': return 'error';
      case 'due_soon': return 'warning';
      case 'partial': return 'info';
      default: return 'primary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircleIcon fontSize="small" />;
      case 'overdue': return <ErrorIcon fontSize="small" />;
      case 'due_soon': return <WarningIcon fontSize="small" />;
      case 'partial': return <WarningIcon fontSize="small" />;
      default: return <CheckCircleIcon fontSize="small" />;
    }
  };

  const formatPaymentStatus = (status: string) => {
    switch (status) {
      case 'paid': return 'Paid';
      case 'overdue': return 'Overdue';
      case 'due_soon': return 'Due Soon';
      case 'partial': return 'Partial Payment';
      default: return status;
    }
  };

  const filteredPaymentData = paymentData.filter((p) => {
    if (paymentTab === 'all') return true;
    if (paymentTab === 'pending') return p.paymentStatus === 'due_soon' || p.paymentStatus === 'partial';
    if (paymentTab === 'overdue') return p.paymentStatus === 'overdue';
    if (paymentTab === 'paid') return p.paymentStatus === 'paid';
    return true;
  });

  const monthOptions = (() => {
    const options: string[] = [];
    const now = new Date();
    for (let i = -6; i <= 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      options.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return options;
  })();

  const handleSelectPayment = (paymentId: string) => {
    setSelectedPayments(prev => 
      prev.includes(paymentId)
        ? prev.filter(id => id !== paymentId)
        : [...prev, paymentId]
    );
  };

  const handleSelectAll = () => {
    const overdueAndDue = paymentData
      .filter(p => p.paymentStatus === 'overdue' || p.paymentStatus === 'due_soon' || p.paymentStatus === 'partial')
      .map(p => p.id);
    
    setSelectedPayments(
      selectedPayments.length === overdueAndDue.length ? [] : overdueAndDue
    );
  };

  const handleRecordPayment = async () => {
    if (!recordPaymentForm.residentId || recordPaymentForm.amount <= 0) {
      alert('Please select a resident and enter a valid amount.');
      return;
    }
    setRecordPaymentSubmitting(true);
    try {
      const [year, month] = selectedMonthYear.split('-').map(Number);
      const monthYear = selectedMonthYear;
      const dueDate = new Date(year, month - 1, 1);
      await fetchWithCacheFallback('/api/payments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          residentId: recordPaymentForm.residentId,
          amount: recordPaymentForm.amount,
          paymentDate: recordPaymentForm.paymentDate,
          dueDate: dueDate.toISOString().split('T')[0],
          monthYear,
          paymentMethod: recordPaymentForm.paymentMethod,
        }),
      });
      offlineCacheService.remove(CACHE_KEYS.PAYMENTS);
      setRecordPaymentDialog(false);
      await loadPaymentData();
    } catch (err) {
      console.error('Failed to record payment:', err);
      alert('Failed to record payment. Please try again.');
    } finally {
      setRecordPaymentSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Payment Management
      </Typography>
      <Typography variant="body1" color="textSecondary" mb={3}>
        Track rent payments, due dates, and send reminders.
      </Typography>

      {loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Loading payment data...
        </Alert>
      )}
      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}

      {!whatsAppApiConfigured && !loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <strong>Manual Reminder Mode:</strong> WhatsApp API is not configured. 
          You can still send reminders manually by clicking the WhatsApp button, 
          which will open WhatsApp with a pre-drafted message.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            mb={2}
            flexWrap="wrap"
            gap={2}
          >
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" flexDirection={{ xs: 'column', sm: 'row' }}>
              <Typography variant="h6">
                Payment Dashboard
              </Typography>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonthYear}
                  label="Month"
                  onChange={(e) => setSelectedMonthYear(e.target.value)}
                >
                  {monthOptions.map((m) => {
                    const [y, mo] = m.split('-');
                    const label = new Date(parseInt(y, 10), parseInt(mo, 10) - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
                    return <MenuItem key={m} value={m}>{label}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              flexWrap="wrap"
              flexDirection={{ xs: 'column', sm: 'row' }}
              sx={{ width: { xs: '100%', md: 'auto' } }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => openRecordPaymentDialog()}
                sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' } }}
              >
                Record Payment
              </Button>
              <Button
                startIcon={<SelectAllIcon />}
                onClick={handleSelectAll}
                sx={{ mr: { sm: 1 }, minHeight: 44, width: { xs: '100%', sm: 'auto' } }}
                size="small"
              >
                {selectedPayments.length > 0 ? 'Deselect All' : 'Select Overdue'}
              </Button>
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                onClick={handleBulkReminder}
                disabled={selectedPayments.length === 0}
                color="success"
                sx={{ minHeight: 44, width: { xs: '100%', sm: 'auto' } }}
              >
                Send Bulk Reminders ({selectedPayments.length})
              </Button>
            </Box>
          </Box>

          <Tabs
            value={paymentTab}
            onChange={(_, v) => setPaymentTab(v as PaymentTabFilter)}
            sx={{
              mb: 2,
              '& .MuiTabs-indicator': { height: 3 },
              '& .Mui-selected': { fontWeight: 600 },
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All" value="all" />
            <Tab label="Pending" value="pending" />
            <Tab label="Overdue" value="overdue" />
            <Tab label="Paid" value="paid" />
          </Tabs>

          {isMobile ? (
            <Box display="flex" flexDirection="column" gap={2}>
              {filteredPaymentData.map((payment) => (
                <Card key={payment.id} variant="outlined" sx={{ overflow: 'visible' }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight={600}>{payment.residentName}</Typography>
                        <Typography variant="body2" color="text.secondary">Bed {payment.bedNumber}</Typography>
                      </Box>
                      <Chip
                        icon={getStatusIcon(payment.paymentStatus)}
                        label={formatPaymentStatus(payment.paymentStatus)}
                        color={getStatusColor(payment.paymentStatus)}
                        size="small"
                      />
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={2} mb={1.5}>
                      <Typography variant="body2">Due: {format(payment.dueDate, 'dd MMM yyyy')}</Typography>
                      <Typography variant="body2" color={payment.paymentStatus === 'overdue' ? 'error.main' : 'text.secondary'}>
                        Grace: {format(payment.gracePeriodEnd, 'dd MMM yyyy')}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <Box>
                        <Typography variant="body2">Amount: ₹{payment.amount.toLocaleString()}</Typography>
                        <Typography variant="body2">Paid: ₹{payment.paidAmount.toLocaleString()}</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ color: payment.paymentStatus === 'overdue' ? 'error.main' : 'inherit' }}>
                          Outstanding: ₹{(payment.amount - payment.paidAmount).toLocaleString()}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Checkbox
                          checked={selectedPayments.includes(payment.id)}
                          onChange={() => handleSelectPayment(payment.id)}
                          disabled={payment.paymentStatus === 'paid'}
                          sx={{ p: 0.5 }}
                        />
                        {payment.paymentStatus !== 'paid' && (
                          <>
                            <Tooltip title="Record Payment">
                              <IconButton color="primary" onClick={() => openRecordPaymentDialog(payment)} sx={{ minWidth: 44, minHeight: 44 }}>
                                <PaymentIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Send Reminder">
                              <IconButton color="success" onClick={() => sendManualReminder(payment)} sx={{ minWidth: 44, minHeight: 44 }}>
                                <WhatsAppIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedPayments.length > 0 && selectedPayments.length < paymentData.length}
                      checked={selectedPayments.length === paymentData.filter(p => p.paymentStatus !== 'paid').length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell><strong>Resident</strong></TableCell>
                  <TableCell><strong>Bed</strong></TableCell>
                  <TableCell><strong>Due Date</strong></TableCell>
                  <TableCell><strong>Grace Period End</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Paid</strong></TableCell>
                  <TableCell><strong>Outstanding</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPaymentData.map((payment) => (
                  <TableRow key={payment.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedPayments.includes(payment.id)}
                        onChange={() => handleSelectPayment(payment.id)}
                        disabled={payment.paymentStatus === 'paid'}
                      />
                    </TableCell>
                    <TableCell>{payment.residentName}</TableCell>
                    <TableCell>{payment.bedNumber}</TableCell>
                    <TableCell>{format(payment.dueDate, 'dd MMM yyyy')}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color={payment.paymentStatus === 'overdue' ? 'error.main' : 'text.secondary'}>
                        {format(payment.gracePeriodEnd, 'dd MMM yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>₹{payment.amount.toLocaleString()}</TableCell>
                    <TableCell>₹{payment.paidAmount.toLocaleString()}</TableCell>
                    <TableCell>
                      <Typography
                        component="strong"
                        sx={{ color: payment.paymentStatus === 'overdue' ? 'error.main' : 'inherit' }}
                      >
                        ₹{(payment.amount - payment.paidAmount).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(payment.paymentStatus)}
                        label={formatPaymentStatus(payment.paymentStatus)}
                        color={getStatusColor(payment.paymentStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {payment.paymentStatus !== 'paid' && (
                        <>
                          <Tooltip title="Record Payment for this resident">
                            <IconButton
                              color="primary"
                              onClick={() => openRecordPaymentDialog(payment)}
                              size="small"
                              sx={{ minWidth: 44, minHeight: 44 }}
                            >
                              <PaymentIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send WhatsApp Reminder">
                            <IconButton
                              color="success"
                              onClick={() => sendManualReminder(payment)}
                              size="small"
                              sx={{ minWidth: 44, minHeight: 44 }}
                            >
                              <WhatsAppIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog open={recordPaymentDialog} onClose={() => setRecordPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Resident</InputLabel>
                <Select
                  value={recordPaymentForm.residentId}
                  label="Resident"
                  onChange={(e) => {
                    const r = residents.find(x => x.id === e.target.value);
                    setRecordPaymentForm(prev => ({
                      ...prev,
                      residentId: e.target.value,
                      amount: r?.monthlyRent ?? prev.amount,
                    }));
                  }}
                >
                  {residents.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name} (Bed {r.bedNumber}) - ₹{r.monthlyRent}/mo
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount (₹)"
                type="number"
                value={recordPaymentForm.amount || ''}
                onChange={(e) => setRecordPaymentForm(prev => ({ ...prev, amount: Number(e.target.value) || 0 }))}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Date"
                type="date"
                value={recordPaymentForm.paymentDate}
                onChange={(e) => setRecordPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={recordPaymentForm.paymentMethod}
                  label="Payment Method"
                  onChange={(e) => setRecordPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value as 'Online' | 'Cash' }))}
                >
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Online">Online</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordPaymentDialog(false)} sx={{ minHeight: 44 }}>Cancel</Button>
          <Button onClick={handleRecordPayment} variant="contained" disabled={recordPaymentSubmitting} sx={{ minHeight: 44 }}>
            {recordPaymentSubmitting ? 'Saving...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Reminder Dialog */}
      <Dialog open={bulkReminderDialog} onClose={() => setBulkReminderDialog(false)}>
        <DialogTitle>Send Bulk Reminders</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to send reminders to {selectedPayments.length} residents. 
            WhatsApp will open sequentially for each resident with a pre-drafted message.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold' }}>
            Selected residents:
          </DialogContentText>
          <Box sx={{ mt: 1 }}>
            {selectedPayments.map(id => {
              const resident = paymentData.find(p => p.id === id);
              return resident ? (
                <Chip 
                  key={id} 
                  label={`${resident.residentName} (${resident.bedNumber})`} 
                  sx={{ mr: 1, mb: 1 }} 
                  size="small"
                />
              ) : null;
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkReminderDialog(false)} sx={{ minHeight: 44 }}>Cancel</Button>
          <Button onClick={processBulkReminders} variant="contained" color="success" sx={{ minHeight: 44 }}>
            Send Reminders
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payments;